import {
  type CustomerDuplicateMatches,
  type DuplicateMatchField,
  formatUsPhone,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { MergeCustomersDialog } from './merge-customers-dialog.js'

type Match = CustomerDuplicateMatches['matches'][number]

const FIELD_LABEL: Record<DuplicateMatchField, string> = {
  name: 'Name',
  businessName: 'Business',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
}

function reason(m: Match): string {
  switch (m.matchKind) {
    case 'exact':
      return 'Exact duplicate'
    case 'details':
      return `Same ${m.matchedFields.map((f) => FIELD_LABEL[f].toLowerCase()).join(' and ')}`
    case 'same_name':
      return 'Same name'
    default:
      return `Similar name (${Math.round((m.similarity ?? 0) * 100)}%)`
  }
}

/** Customers list → click a "Similar name" / "Possible duplicate" tag (t1344):
 *  pick which matching record to merge with, then the usual merge dialog. */
export function CustomerMatchMergeDialog({
  customerId,
  onClose,
}: {
  customerId: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const [chosen, setChosen] = useState<Match | null>(null)
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.customers.duplicateMatches(customerId),
    queryFn: () => api.get<CustomerDuplicateMatches>(`/customers/${customerId}/duplicate-matches`),
  })

  if (data && chosen) {
    return (
      <MergeCustomersDialog
        a={data.customer}
        b={chosen.record}
        onClose={() => setChosen(null)}
        onMerged={onClose}
      />
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {data ? `Possible matches for ${data.customer.name}` : 'Possible matches'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-2">
          {isLoading ? (
            <p className="text-sm text-foreground-muted">Looking for matches…</p>
          ) : isError || !data ? (
            <p className="text-sm text-danger">Could not load the matches.</p>
          ) : data.matches.length === 0 ? (
            <p className="text-sm text-foreground-muted">
              No matches any more — this customer may already have been merged.
            </p>
          ) : (
            <>
              <p className="text-sm text-foreground-muted">
                Pick the customer to merge with. You'll choose which record to keep and see what
                moves before anything happens. A merge can be undone from Merge history.
              </p>
              {data.matches.map((m) => (
                <div
                  key={m.record.id}
                  className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {m.record.name}
                      <Badge tone={m.matchKind === 'similar_name' ? 'neutral' : 'warning'}>
                        {reason(m)}
                      </Badge>
                    </p>
                    {m.record.businessName ? (
                      <p className="text-xs text-foreground-muted">{m.record.businessName}</p>
                    ) : null}
                    {m.record.phone ? (
                      <p className="text-xs text-foreground-muted">
                        {formatUsPhone(m.record.phone)}
                      </p>
                    ) : null}
                    {m.record.email ? (
                      <p className="truncate text-xs text-foreground-muted">{m.record.email}</p>
                    ) : null}
                    <p className="text-xs text-foreground-muted">
                      {m.record.orderCount} orders, {m.record.repairTicketCount} repairs,{' '}
                      {m.record.supportTicketCount} support tickets, {m.record.subscriptionCount}{' '}
                      subscriptions
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => setChosen(m)}>
                    Merge…
                  </Button>
                </div>
              ))}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              onClose()
              void navigate({ to: '/customers/cleanup' })
            }}
          >
            Open Duplicates &amp; cleanup
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
